import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useUserAccess } from '../../context/UserAccessContext';
import {
  cloneWebCreatorTemplate,
  createWebCreatorSite,
  createWebCreatorTemplate,
  deleteWebCreatorSite,
  deleteWebCreatorTemplate,
  fetchWebCreatorSites,
  fetchWebCreatorTemplates,
  updateWebCreatorSite,
} from '../../api/webCreator';
import WebCreatorLayout from './WebCreatorLayout';
import SiteLibraryCard from './components/SiteLibraryCard';
import TemplateLibraryCard from './components/TemplateLibraryCard';
import PreviewModal from './components/PreviewModal';
import JustOnePageLogo from './components/JustOnePageLogo';
import { JUST_ONE_PAGE_TAGLINE } from './justOnePageBrand';
import { migrateSite } from './utils/defaults';

const HOME_TABS = [
  { id: 'sites', label: 'Mes sites', icon: 'FolderOpen' },
  { id: 'templates', label: 'Templates', icon: 'LayoutTemplate' },
];

const FILTERS = [
  { id: 'all', label: 'Tous' },
  { id: 'published', label: 'En ligne' },
  { id: 'draft', label: 'Brouillons' },
];

export default function WebCreatorHome() {
  const navigate = useNavigate();
  const { user } = useUserAccess();
  const isAdmin = user?.permission === 'admin';

  const [homeTab, setHomeTab] = useState('sites');
  const [sites, setSites] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [templateQuery, setTemplateQuery] = useState('');
  const [previewSite, setPreviewSite] = useState(null);
  const [previewViewport, setPreviewViewport] = useState('desktop');

  const loadSites = async () => {
    setLoadingSites(true);
    try {
      const res = await fetchWebCreatorSites();
      setSites(res?.data?.sites || []);
    } finally {
      setLoadingSites(false);
    }
  };

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await fetchWebCreatorTemplates();
      setTemplates(res?.data?.templates || []);
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    loadSites();
  }, []);

  useEffect(() => {
    if (homeTab === 'templates') {
      loadTemplates();
    }
  }, [homeTab]);

  const stats = useMemo(() => {
    const published = sites.filter((s) => s.published).length;
    return { total: sites.length, published, drafts: sites.length - published };
  }, [sites]);

  const filteredSites = useMemo(() => {
    let list = sites;
    if (filter === 'published') list = list.filter((s) => s.published);
    if (filter === 'draft') list = list.filter((s) => !s.published);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (s) =>
          (s.title || '').toLowerCase().includes(q) ||
          (s.slug || '').toLowerCase().includes(q) ||
          (s.description || '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [sites, filter, query]);

  const filteredTemplates = useMemo(() => {
    const q = templateQuery.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) =>
        (t.title || '').toLowerCase().includes(q) ||
        (t.templateDescription || '').toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q),
    );
  }, [templates, templateQuery]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await createWebCreatorSite({ title: 'Mon site' });
      const site = res?.data?.site;
      if (site?._id) navigate(`/web-creator/edit/${site._id}`);
    } catch (err) {
      alert(err.message || 'Impossible de créer le site.');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateTemplate = async () => {
    const title = window.prompt('Nom du template', 'Nouveau template');
    if (!title?.trim()) return;
    setCreatingTemplate(true);
    try {
      const res = await createWebCreatorTemplate({ title: title.trim() });
      const template = res?.data?.site;
      if (template?._id) {
        await loadTemplates();
        navigate(`/web-creator/edit/${template._id}`);
      }
    } catch (err) {
      alert(err.message || 'Impossible de créer le template.');
    } finally {
      setCreatingTemplate(false);
    }
  };

  const handleDelete = async (siteId) => {
    if (!window.confirm('Supprimer définitivement ce site ?')) return;
    setBusyId(siteId);
    try {
      await deleteWebCreatorSite(siteId);
      await loadSites();
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Supprimer définitivement ce template ?')) return;
    setBusyId(templateId);
    try {
      await deleteWebCreatorTemplate(templateId);
      await loadTemplates();
    } catch (err) {
      alert(err.message || 'Impossible de supprimer le template.');
    } finally {
      setBusyId(null);
    }
  };

  const handleUseTemplate = async (template) => {
    setBusyId(template._id);
    try {
      const res = await cloneWebCreatorTemplate(template._id, {
        title: `${template.title || 'Mon site'} (copie)`,
      });
      const site = res?.data?.site;
      if (site?._id) navigate(`/web-creator/edit/${site._id}`);
    } catch (err) {
      alert(err.message || 'Impossible de créer le site depuis le template.');
    } finally {
      setBusyId(null);
    }
  };

  const handlePreviewTemplate = (template) => {
    setPreviewSite(migrateSite(template));
    setPreviewViewport('desktop');
  };

  const handlePublish = async (siteId) => {
    setBusyId(siteId);
    try {
      await updateWebCreatorSite(siteId, { published: true });
      await loadSites();
    } catch (err) {
      alert(err.message || 'Impossible de publier le site.');
    } finally {
      setBusyId(null);
    }
  };

  const handleUnpublish = async (siteId) => {
    if (
      !window.confirm(
        'Dépublier ce site ? Il ne sera plus accessible publiquement.',
      )
    ) {
      return;
    }
    setBusyId(siteId);
    try {
      await updateWebCreatorSite(siteId, { published: false });
      await loadSites();
    } catch (err) {
      alert(err.message || 'Impossible de dépublier le site.');
    } finally {
      setBusyId(null);
    }
  };

  const loading = homeTab === 'sites' ? loadingSites : loadingTemplates;

  return (
    <WebCreatorLayout>
      {previewSite ? (
        <PreviewModal
          site={previewSite}
          viewport={previewViewport}
          onViewportChange={setPreviewViewport}
          onClose={() => setPreviewSite(null)}
        />
      ) : null}

      <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-muted/30 via-background to-background">
        <div className="border-b border-border/60 bg-card/80 backdrop-blur-sm">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="space-y-3 max-w-2xl">
                <JustOnePageLogo size="xl" />
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  {JUST_ONE_PAGE_TAGLINE}
                  {' '}
                  {homeTab === 'templates'
                    ? 'Partez d’un template prêt à l’emploi et personnalisez-le en quelques clics.'
                    : 'Tous vos sites au même endroit, prêts à éditer en un clic.'}
                </p>
              </div>

              <div className="flex flex-wrap gap-2 shrink-0">
                {homeTab === 'sites' ? (
                  <Button size="lg" onClick={handleCreate} disabled={creating} className="shadow-md">
                    <Icon name="Plus" size={18} className="mr-2" />
                    {creating ? 'Création…' : 'Nouveau site'}
                  </Button>
                ) : isAdmin ? (
                  <Button
                    size="lg"
                    onClick={handleCreateTemplate}
                    disabled={creatingTemplate}
                    className="shadow-md"
                  >
                    <Icon name="Plus" size={18} className="mr-2" />
                    {creatingTemplate ? 'Création…' : 'Nouveau template'}
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-1.5 p-1 rounded-xl bg-muted/50 border border-border/60 w-fit">
              {HOME_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setHomeTab(tab.id)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
                    homeTab === tab.id
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  {tab.label}
                </button>
              ))}
            </div>

            {homeTab === 'sites' && !loadingSites && sites.length > 0 ? (
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Sites', value: stats.total, icon: 'FolderOpen' },
                  { label: 'En ligne', value: stats.published, icon: 'Globe' },
                  { label: 'Brouillons', value: stats.drafts, icon: 'FileEdit' },
                  {
                    label: 'Blocs total',
                    value: sites.reduce(
                      (n, s) =>
                        n +
                        (s.sections || []).reduce(
                          (m, sec) => m + (sec.components?.length || 0),
                          0,
                        ) +
                        (s.pageMenu ? 1 : 0),
                      0,
                    ),
                    icon: 'LayoutGrid',
                  },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-xl border border-border/70 bg-background/80 px-4 py-3 flex items-center gap-3"
                  >
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon name={stat.icon} size={18} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-xl font-semibold text-foreground leading-none">{stat.value}</p>
                      <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-6">
          {homeTab === 'sites' ? (
            <>
              {sites.length > 0 ? (
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                  <div className="flex flex-wrap gap-1.5 p-1 rounded-xl bg-muted/50 border border-border/60 w-fit">
                    {FILTERS.map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => setFilter(f.id)}
                        className={`px-3.5 py-1.5 rounded-lg text-xs font-medium transition-smooth ${
                          filter === f.id
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>

                  <div className="relative w-full sm:max-w-xs">
                    <Icon
                      name="Search"
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    />
                    <input
                      type="search"
                      placeholder="Rechercher un site…"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      className="w-full rounded-xl border border-border bg-background pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                </div>
              ) : null}

              {loading ? (
                <div className="flex flex-col items-center justify-center py-24 gap-3 text-muted-foreground">
                  <Icon name="Loader2" size={28} className="animate-spin text-primary" />
                  <p className="text-sm">Chargement de votre bibliothèque…</p>
                </div>
              ) : sites.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-card/50 p-12 sm:p-16 text-center space-y-5 max-w-lg mx-auto mt-8">
                  <JustOnePageLogo size="lg" className="mx-auto" />
                  <div className="space-y-2">
                    <h2 className="text-lg font-semibold text-foreground">Votre bibliothèque est vide</h2>
                    <p className="text-sm text-muted-foreground">
                      Créez votre première landing page par glisser-déposer, ou partez d’un template
                      e-commerce prêt à l’emploi.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 justify-center">
                    <Button size="lg" onClick={handleCreate} disabled={creating}>
                      <Icon name="Plus" size={18} className="mr-2" />
                      Créer mon premier site
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={() => setHomeTab('templates')}
                    >
                      <Icon name="LayoutTemplate" size={18} className="mr-2" />
                      Voir les templates
                    </Button>
                  </div>
                </div>
              ) : filteredSites.length === 0 ? (
                <div className="rounded-xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">
                  Aucun site ne correspond à votre recherche.
                </div>
              ) : (
                <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                  {filteredSites.map((site) => (
                    <SiteLibraryCard
                      key={site._id}
                      site={site}
                      busyId={busyId}
                      onDelete={handleDelete}
                      onPublish={handlePublish}
                      onUnpublish={handleUnpublish}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                <p className="text-sm text-muted-foreground max-w-xl">
                  {isAdmin
                    ? 'Créez et modifiez les templates disponibles pour tous les utilisateurs.'
                    : 'Choisissez un template : une copie sera créée dans votre bibliothèque, prête à personnaliser.'}
                </p>
                <div className="relative w-full sm:max-w-xs">
                  <Icon
                    name="Search"
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <input
                    type="search"
                    placeholder="Rechercher un template…"
                    value={templateQuery}
                    onChange={(e) => setTemplateQuery(e.target.value)}
                    className="w-full rounded-xl border border-border bg-background pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>

              {loading ? (
                <div className="flex flex-col items-center justify-center py-24 gap-3 text-muted-foreground">
                  <Icon name="Loader2" size={28} className="animate-spin text-primary" />
                  <p className="text-sm">Chargement des templates…</p>
                </div>
              ) : filteredTemplates.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border bg-card/50 p-12 text-center space-y-4 max-w-lg mx-auto">
                  <Icon name="LayoutTemplate" size={40} className="mx-auto text-muted-foreground/40" />
                  <div className="space-y-2">
                    <h2 className="text-lg font-semibold text-foreground">Aucun template</h2>
                    <p className="text-sm text-muted-foreground">
                      {templateQuery.trim()
                        ? 'Aucun template ne correspond à votre recherche.'
                        : isAdmin
                          ? 'Créez le premier template pour vos utilisateurs.'
                          : 'Les templates seront bientôt disponibles.'}
                    </p>
                  </div>
                  {isAdmin && !templateQuery.trim() ? (
                    <Button onClick={handleCreateTemplate} disabled={creatingTemplate}>
                      <Icon name="Plus" size={18} className="mr-2" />
                      Créer un template
                    </Button>
                  ) : null}
                </div>
              ) : (
                <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                  {filteredTemplates.map((template) => (
                    <TemplateLibraryCard
                      key={template._id}
                      template={template}
                      isAdmin={isAdmin}
                      busyId={busyId}
                      onPreview={handlePreviewTemplate}
                      onUse={handleUseTemplate}
                      onDelete={handleDeleteTemplate}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </WebCreatorLayout>
  );
}
